/**
 * OrejaDB - IndexedDB persistence layer for Oreja audio blobs and session state.
 *
 * Provides resilience for audio recordings and in-progress session data.
 * Uses the `idb` library for a promise-based IndexedDB API.
 *
 * DB name: "oreja", version: 1
 * Stores: "blobs" (keyPath: sessionId), "sessions" (keyPath: sessionId)
 */

import { openDB, type IDBPDatabase } from 'idb';

const DB_NAME = 'oreja';
const DB_VERSION = 1;

/** Default max age for cleanup: 24 hours in milliseconds. */
const DEFAULT_MAX_AGE_MS = 24 * 60 * 60 * 1000;

/** Record stored in the "blobs" object store. */
export interface BlobRecord {
  /** Session ID (primary key). */
  sessionId: string;
  /** The audio blob. */
  blob: Blob;
  /** ISO timestamp when the blob was uploaded to storage, or null if pending. */
  uploadedAt: string | null;
  /** ISO timestamp when the record was created. */
  createdAt: string;
}

/** Record stored in the "sessions" object store. */
export interface SessionStateRecord {
  /** Session ID (primary key). */
  sessionId: string;
  /** Current phase of the session. */
  phase: string;
  /** Associated client ID. */
  clientId?: string;
  /** Transcript text. */
  transcript?: string;
  /** AI proposals (stored as unknown to avoid coupling to specific shape). */
  proposals?: unknown;
  /** Allow additional session-specific fields. */
  [key: string]: unknown;
}

/**
 * Static helper class for Oreja IndexedDB operations.
 *
 * All methods are static and open the database as needed.
 */
export class OrejaDB {
  /**
   * Opens the Oreja IndexedDB database, creating object stores on first run.
   *
   * @returns The opened IDBPDatabase instance.
   */
  static async open(): Promise<IDBPDatabase> {
    return openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('blobs')) {
          db.createObjectStore('blobs', { keyPath: 'sessionId' });
        }
        if (!db.objectStoreNames.contains('sessions')) {
          db.createObjectStore('sessions', { keyPath: 'sessionId' });
        }
      },
    });
  }

  /**
   * Saves an audio blob for a session.
   *
   * @param sessionId - The session identifier.
   * @param blob - The audio Blob to store.
   */
  static async saveBlob(sessionId: string, blob: Blob): Promise<void> {
    const db = await OrejaDB.open();
    try {
      const record: BlobRecord = {
        sessionId,
        blob,
        uploadedAt: null,
        createdAt: new Date().toISOString(),
      };
      await db.put('blobs', record);
    } finally {
      db.close();
    }
  }

  /**
   * Retrieves a blob record by session ID.
   *
   * @param sessionId - The session identifier.
   * @returns The BlobRecord or null if not found.
   */
  static async getBlob(sessionId: string): Promise<BlobRecord | null> {
    const db = await OrejaDB.open();
    try {
      const record = await db.get('blobs', sessionId);
      return (record as BlobRecord) ?? null;
    } finally {
      db.close();
    }
  }

  /**
   * Marks a blob as uploaded by setting the uploadedAt timestamp.
   *
   * @param sessionId - The session identifier.
   */
  static async markBlobUploaded(sessionId: string): Promise<void> {
    const db = await OrejaDB.open();
    try {
      const record = await db.get('blobs', sessionId);
      if (record) {
        record.uploadedAt = new Date().toISOString();
        await db.put('blobs', record);
      }
    } finally {
      db.close();
    }
  }

  /**
   * Deletes a blob record by session ID.
   *
   * @param sessionId - The session identifier.
   */
  static async deleteBlob(sessionId: string): Promise<void> {
    const db = await OrejaDB.open();
    try {
      await db.delete('blobs', sessionId);
    } finally {
      db.close();
    }
  }

  /**
   * Saves or updates session state.
   *
   * @param sessionId - The session identifier.
   * @param state - The session state to persist.
   */
  static async saveSessionState(
    sessionId: string,
    state: SessionStateRecord
  ): Promise<void> {
    const db = await OrejaDB.open();
    try {
      await db.put('sessions', { ...state, sessionId });
    } finally {
      db.close();
    }
  }

  /**
   * Retrieves session state by session ID.
   *
   * @param sessionId - The session identifier.
   * @returns The SessionStateRecord or null if not found.
   */
  static async getSessionState(sessionId: string): Promise<SessionStateRecord | null> {
    const db = await OrejaDB.open();
    try {
      const record = await db.get('sessions', sessionId);
      return (record as SessionStateRecord) ?? null;
    } finally {
      db.close();
    }
  }

  /**
   * Deletes session state by session ID.
   *
   * @param sessionId - The session identifier.
   */
  static async deleteSessionState(sessionId: string): Promise<void> {
    const db = await OrejaDB.open();
    try {
      await db.delete('sessions', sessionId);
    } finally {
      db.close();
    }
  }

  /**
   * Removes blob records that were uploaded more than maxAgeMs ago.
   * Blobs that have not been uploaded (uploadedAt is null) are kept.
   *
   * @param maxAgeMs - Maximum age in milliseconds (default: 24 hours).
   * @returns The number of records cleaned up.
   */
  static async cleanupOldSessions(maxAgeMs: number = DEFAULT_MAX_AGE_MS): Promise<number> {
    const db = await OrejaDB.open();
    try {
      const tx = db.transaction('blobs', 'readwrite');
      const store = tx.objectStore('blobs');
      let cursor = await store.openCursor();
      let cleaned = 0;
      const cutoff = Date.now() - maxAgeMs;

      while (cursor) {
        const record = cursor.value as BlobRecord;
        if (record.uploadedAt && new Date(record.uploadedAt).getTime() < cutoff) {
          await cursor.delete();
          cleaned++;
        }
        cursor = await cursor.continue();
      }

      await tx.done;
      return cleaned;
    } finally {
      db.close();
    }
  }
}
